import React, { useCallback, useState } from 'react';
import { FileError, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, Preloader, Text, useResponsive } from '@common-fe/common-fe';

import { ACCEPTED_FILE_TYPES, FileImage, MAX_SIZE_OF_FILE } from '@/common/constants';
import { BackButton, Modal } from '@/components';
import DropScanContent from '@/components/controls/DropScan/DropScanContent';
import prepareFile from '@/components/controls/DropScan/prepareFile';
import UploadFileErrorPopup from '@/components/controls/DropScan/UploadFileErrorPopup';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';
import useOnDrop from '@/components/controls/DropScan/useOnDrop';
import { DocumentTypesEnum, LMN_UPLOAD_STORE_KEY } from '@/modules/MyDocuments/myDocuments.types';

import useSetFileFromDocumentStream from '../../ReimburseMe/hooks/useSetFileFromDocumentStream';
import { UploadFileQRCodeSection } from '../../ReimburseMe/UploadFileQRCode';

import useLinkLmnToClaim from './hooks/useLinkLmnToClaim';
import TransactionLinkedLmnList from './LinkedLmnList/TransactionLinkedLmnList';

interface Props {
  onSetVisible: (value: boolean) => void;
  setIsTransactionLmnPopupVisible: (value: boolean) => void;
  claimId?: string;
  employeeId?: string;
  dateOfService?: string;
}

const TransactionSelectLmnFilePopup: React.FC<Props> = ({
  onSetVisible,
  setIsTransactionLmnPopupVisible,
  claimId,
  employeeId,
  dateOfService,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useResponsive();

  const onSetFileFromStream = useCallback(() => {
    setIsTransactionLmnPopupVisible(true);
    onSetVisible(false);
  }, [setIsTransactionLmnPopupVisible, onSetVisible]);

  useSetFileFromDocumentStream({ storeKey: LMN_UPLOAD_STORE_KEY, onSetFileFromStream });
  const { handleSetValues } = useFilesInDocumentStore(LMN_UPLOAD_STORE_KEY);

  const handleClose = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  const { selectedLmnId, handleSelectLmn, handleLinkLmnToClaim, isLinkLmnToClaimLoading } = useLinkLmnToClaim(
    { claimId, onSuccess: handleClose }
  );

  const onFilesAdded = useCallback(
    (acceptedFiles: FileImage[]) => {
      if (acceptedFiles?.length) {
        handleSetValues({ files: acceptedFiles.map(prepareFile) });
        setIsTransactionLmnPopupVisible(true);
        handleClose();
      }
    },
    [handleSetValues, setIsTransactionLmnPopupVisible, handleClose]
  );

  const [isFileUploadErrorModalVisible, setIsFileUploadErrorModalVisible] = useState(false);
  const [fileUploadError, setFileUploadError] = useState<FileError | undefined>();

  const onFileUploadError = useCallback((error?: FileError) => {
    setFileUploadError(error);
    setIsFileUploadErrorModalVisible(true);
  }, []);

  const onCloseFileUploadError = useCallback(() => {
    setFileUploadError(undefined);
    setIsFileUploadErrorModalVisible(false);
  }, []);

  const { onDrop } = useOnDrop({
    onFilesAdded,
    onFileUploadError,
    disableFilesUpload: true,
    storeKey: LMN_UPLOAD_STORE_KEY,
  });
  const dropzoneProps = useDropzone({
    accept: ACCEPTED_FILE_TYPES,
    maxSize: MAX_SIZE_OF_FILE,
    onDrop,
    multiple: false,
    maxFiles: 1,
  });

  return (
    <>
      <Modal testId="transaction-select-lmn-file" visible onSetVisible={onSetVisible} isFullScreen={isMobile}>
        {isMobile ? (
          <Box margin={{ bottom: 'spacing16' }}>
            <BackButton testId="select-lmn-file-popup" onClick={handleClose} />
          </Box>
        ) : null}

        <Text size="2xl" textAlign={isMobile ? 'start' : 'center'} weight={700} data-testId="title_modal">
          {t('Link an Existing LMN')}
        </Text>

        <Box
          width={{ max: 'none' }}
          margin={{
            top: isMobile ? 'spacing16' : 'spacing24',
            horizontal: isMobile ? '-14px' : undefined,
          }}
          background="module"
          round="moduleRound"
          pad={isMobile ? 'spacing12' : 'spacing24'}
        >
          <TransactionLinkedLmnList
            claimId={claimId}
            employeeId={employeeId}
            dateOfService={dateOfService}
            selectedLmnId={selectedLmnId}
            setSelectedLmnId={handleSelectLmn}
          />

          <Box
            margin={{ top: 'spacing24' }}
            direction="row"
            gap="spacing12"
            background="canvas"
            round="container1Round"
            pad="spacing16"
          >
            <Box width="50%">
              <DropScanContent wrapperProps={{ style: { padding: 0 } }} {...dropzoneProps} />
            </Box>
            <Box width="50%">
              <UploadFileQRCodeSection
                documentType={DocumentTypesEnum.LETTER_OF_MEDICAL_NECESSITY}
                storeKey={LMN_UPLOAD_STORE_KEY}
              />
            </Box>
          </Box>
        </Box>

        <Box margin={{ top: 'spacing24' }} gap="spacing12" direction="row" align="center" justify="end">
          <Box width={{ width: '100%', max: isMobile ? '50%' : '160px' }}>
            <AppButton
              testId="transaction-select-lmn-file_close"
              width="100%"
              buttonType="secondary"
              onClick={handleClose}
            >
              Close
            </AppButton>
          </Box>
          <Box width={{ width: '100%', max: isMobile ? '50%' : '160px' }}>
            <AppButton
              testId="transaction-select-lmn-file_save"
              width="100%"
              onClick={handleLinkLmnToClaim}
              disabled={isLinkLmnToClaimLoading}
            >
              {isLinkLmnToClaimLoading ? <Preloader color="white" /> : 'Save'}
            </AppButton>
          </Box>
        </Box>
      </Modal>

      {isFileUploadErrorModalVisible && (
        <UploadFileErrorPopup fileError={fileUploadError} onClose={onCloseFileUploadError} />
      )}
    </>
  );
};

export default TransactionSelectLmnFilePopup;
